import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import SearchableSelect from './SearchableSelect';
import { Button } from './ui/button';
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Bar,
    Legend,
} from 'recharts';
import {
    TrendingUp,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertCircle,
    BarChart3
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { workspaceAPI } from '../lib/api';

const MONTH_NAMES = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

// Custom tooltip for the S-Curve chart
const SCurveTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;

    return (
        <div className="bg-white border border-brand-border rounded-lg shadow-lg p-3 text-sm">
            <p className="font-semibold text-brand-text-primary mb-1">
                {format(parseISO(data.date), 'd MMMM yyyy', { locale: idLocale })}
            </p>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2F6F63' }} />
                    <span className="text-brand-text-muted">Kumulatif:</span>
                    <span className="font-semibold text-brand-text-primary">{data.cumulative}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#6EC1E4' }} />
                    <span className="text-brand-text-muted">Harian:</span>
                    <span className="font-semibold text-brand-text-primary">{data.daily}</span>
                </div>
                {data.target_cumulative !== undefined && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F97316' }} />
                        <span className="text-brand-text-muted">Target Kumulatif:</span>
                        <span className="font-semibold text-brand-text-primary">{data.target_cumulative}</span>
                    </div>
                )}
                {data.target !== undefined && (
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
                        <span className="text-brand-text-muted">Target Harian:</span>
                        <span className="font-semibold text-brand-text-primary">{data.target}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function SCurveChart({ workspaceId, spaces = [], defaultSpaceId = null, singleSpaceMode = false }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [selectedSpaceId, setSelectedSpaceId] = useState(defaultSpaceId || 'all');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        setError(null);
        try {
            const spaceIdParam = selectedSpaceId === 'all' ? null : selectedSpaceId;
            const res = await workspaceAPI.getStatTimeline(workspaceId, year, month, spaceIdParam);
            setData(res.data?.data || []);
        } catch (err) {
            console.error('Failed to load S-curve data:', err);
            setError('Gagal memuat data kurva S');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [workspaceId, year, month, selectedSpaceId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePrevMonth = () => {
        if (month === 1) {
            setMonth(12);
            setYear(y => y - 1);
        } else {
            setMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (month === 12) {
            setMonth(1);
            setYear(y => y + 1);
        } else {
            setMonth(m => m + 1);
        }
    };

    const maxCumulative = data.length > 0 ? Math.max(...data.map(d => d.cumulative)) : 0;
    const maxDaily = data.length > 0 ? Math.max(...data.map(d => d.daily)) : 0;
    const totalDone = data.length > 0 ? data[data.length - 1]?.cumulative || 0 : 0;
    const totalTarget = data.length > 0 ? data[data.length - 1]?.target || 0 : 0;

    return (
        <Card className="border-brand-border shadow-card">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-brand-primary" />
                        Kurva S - Progress Bulanan
                    </CardTitle>
                    <div className="flex items-center gap-3">
                        {/* Space Filter - only show if not in single space mode */}
                        {!singleSpaceMode && spaces.length > 0 && (
                            <SearchableSelect
                                value={selectedSpaceId}
                                onChange={setSelectedSpaceId}
                                options={[
                                    { value: 'all', label: 'Semua Space' },
                                    ...spaces.map(space => ({
                                        value: space.id,
                                        label: space.name,
                                        color: space.color,
                                    })),
                                ]}
                                placeholder="Pilih Space"
                                className="w-[180px]"
                            />
                        )}
                        {/* Month Navigation */}
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handlePrevMonth}
                                className="h-8 w-8 p-0"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm font-medium text-brand-text-primary min-w-[140px] text-center">
                                {MONTH_NAMES[month - 1]} {year}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleNextMonth}
                                className="h-8 w-8 p-0"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center justify-center h-[300px]">
                        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-brand-text-muted">
                        <AlertCircle className="w-8 h-8 mb-2 text-brand-error" />
                        <p className="text-sm">{error}</p>
                        <Button variant="outline" size="sm" className="mt-2" onClick={fetchData}>
                            Coba Lagi
                        </Button>
                    </div>
                ) : data.length === 0 || maxCumulative === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-brand-text-muted">
                        <BarChart3 className="w-10 h-10 mb-3 opacity-40" />
                        <p className="text-sm">Tidak ada data untuk periode ini</p>
                        <p className="text-xs mt-1">Kurva S menampilkan task yang sudah selesai</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-brand-primary" />
                                <span className="text-brand-text-muted">Total Selesai:</span>
                                <span className="font-semibold text-brand-text-primary">{totalDone} task</span>
                            </div>
                            {totalTarget > 0 && (
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                                    <span className="text-brand-text-muted">Target:</span>
                                    <span className="font-semibold text-brand-text-primary">{totalTarget} task</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-blue-400" />
                                <span className="text-brand-text-muted">Max Harian:</span>
                                <span className="font-semibold text-brand-text-primary">{maxDaily} task</span>
                            </div>
                        </div>

                        {/* Chart */}
                        <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2F6F63" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#2F6F63" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(value) => format(parseISO(value), 'd', { locale: idLocale })}
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    stroke="#9CA3AF"
                                />
                                <YAxis
                                    yAxisId="left"
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    stroke="#9CA3AF"
                                    label={{ value: 'Kumulatif', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6B7280' } }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    stroke="#9CA3AF"
                                    label={{ value: 'Harian / Target', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#6B7280' } }}
                                />
                                <Tooltip content={<SCurveTooltip />} />
                                <Legend
                                    wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }}
                                    iconType="circle"
                                />
                                <Area
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="cumulative"
                                    stroke="#2F6F63"
                                    strokeWidth={2.5}
                                    fill="url(#colorCumulative)"
                                    name="Kumulatif"
                                />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="target_cumulative"
                                    stroke="#F97316"
                                    strokeWidth={2.5}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    name="Target Kumulatif"
                                />
                                <Bar
                                    yAxisId="right"
                                    dataKey="daily"
                                    fill="#6EC1E4"
                                    radius={[4, 4, 0, 0]}
                                    barSize={20}
                                    name="Harian"
                                />
                                <Bar
                                    yAxisId="right"
                                    dataKey="target"
                                    fill="#F59E0B"
                                    radius={[4, 4, 0, 0]}
                                    barSize={20}
                                    name="Target Harian"
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
